import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useState } from "react";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { IIncident } from "../../../types/api/incident";
import { QData } from "../../../types/basic-type-list";
import { TResponseWithQData, TResponse } from "../../../types/common";

// ── Incident list ──────────────────────────────────────────────────────────

export const useIncidentList = (qData: QData) => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const result = useQuery<TResponseWithQData<IIncident[]>>({
    queryKey: [API_ENDPOINTS.INCIDENT_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await axios.get(API_ENDPOINTS.INCIDENT_LIST, { params: queryParams });
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data ?? [],
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });

  return {
    ...result,
    refetchWithQuery: (params: Partial<QData>) => setQueryParams(params as QData),
  };
};

// ── Incident detail ────────────────────────────────────────────────────────

export const useIncidentDetail = (
  id: number | null,
  options?: Omit<UseQueryOptions<TResponse<IIncident>, Error>, "queryKey" | "queryFn">
) =>
  useQuery<TResponse<IIncident>, Error>({
    queryKey: [API_ENDPOINTS.INCIDENT_DETAIL, id],
    queryFn: async () => {
      const res = await axios.get(`${API_ENDPOINTS.INCIDENT_DETAIL}/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    ...options,
  });

// ── Update status ──────────────────────────────────────────────────────────

export const useIncidentUpdateStatus = ({ onSuccess, onError }: { onSuccess?: () => void; onError?: (e: any) => void } = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; status: string; riddor_report_date?: string; riddor_ref_number?: string }) => {
      const res = await axios.post(API_ENDPOINTS.INCIDENT_UPDATE_STATUS, data);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.INCIDENT_LIST] });
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.INCIDENT_DETAIL, vars.id] });
      onSuccess?.();
    },
    onError,
  });
};
